<template>
  <div class="files layout-container">
    <div class="layout-content">
      <div class="files-header">
        <FriedH2 class="hidden-sm-and-down">{{ t('frame_files') }}</FriedH2>
        <SearchBar
          class="file-search"
          data-external="file-search-published"
          identifier="files-published"
          :loading="searchLoading"
          :current-search-term="currentSearchTerm"
          @update-search="searchFiles(0, $event)"
        />
        <div class="button-container">
          <FriedButton
            :button-type="ButtonType.Secondary"
            :disabled="totalFilesToUpload > 0"
            class="hidden-xs-and-down upload-button"
            data-external="files-upload-file-button"
            @click="showUploadModal = true"
          >
            <template #leftIcon>
              <FriedIcon icon="upload" />
            </template>
            {{ t('Upload file') }}
          </FriedButton>
          <FriedIconButton
            data-external="participant-filter-show-button"
            class="filter-button"
            :active="filter.participantIds.length > 0"
            @click="showFilterDrawer = true"
          >
            <FriedIcon icon="filter" />
          </FriedIconButton>
        </div>
      </div>
      <FileTable
        :files="files"
        :user="owner"
        :participants="participants"
        :sort-by="filter.sortBy"
        :filter="filter"
        :participant-id="participantId"
        :total-files="totalFiles"
        :sort-order="filter.sortOrder"
        @delete="handleDelete"
        @download="handleDownload"
        @load="updateFilter"
        @upload="showUploadModal = true"
      />
      <UploadModal
        v-if="showUploadModal"
        :is-upload-restricted="isUploadRestricted"
        :file-size-limit="fileSizeLimit"
        @close="showUploadModal = false"
        @upload-files="handleUpload"
      />
      <TransitionSlideInOut appear :origin="SlideOrigin.Right">
        <ParticipantFilter
          v-if="showFilterDrawer"
          :participants="allParticipants"
          :selected-participant-ids="filter.participantIds"
          @filter="handleFilterByParticipants"
          @close="showFilterDrawer = false"
        />
      </TransitionSlideInOut>
    </div>
    <FriedButton
      :disabled="totalFilesToUpload > 0"
      data-external="files-upload-file-button"
      class="hidden-xs-and-up floating-upload-button"
      @click="showUploadModal = true"
    >
      <template #leftIcon>
        <FriedIcon icon="upload" />
      </template>
      {{ t('Upload file') }}
    </FriedButton>
  </div>
</template>
<script lang="ts">
import type { Participant } from '@getaccept/dsr-shared-new';
import { Feature } from '@getaccept/lib-shared-new/src/feature-restriction/types/feature';
import { FileConstants } from '@getaccept/dsr-shared-new/src/files/constants/file.constants';
import FileTable from '@getaccept/dsr-shared-new/src/files/components/FileTable.vue';
import ParticipantFilter from '@getaccept/dsr-shared-new/src/files/components/ParticipantFilter.vue';
import UploadModal from '@getaccept/dsr-shared-new/src/files/components/UploadModal.vue';
import { FileHelper } from '@getaccept/dsr-shared-new/src/files/helpers/file.helper';
import type { LoadFilesPayload } from '@getaccept/dsr-shared-new/src/files/types/load-files-payload';
import { ButtonType, SlideOrigin } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import SearchBar from '@getaccept/lib-shared-new/src/search/components/SearchBar.vue';
import { useToast } from '@getaccept/lib-shared-new/src/toast';
import { useAsyncState } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import type { PropType } from 'vue';
import { computed, defineComponent, ref } from 'vue';
import { useFeatureStore } from '@getaccept/lib-shared-new/src/feature-restriction/store/feature.store';
import { FileService } from '../../../api/files/services/file.service';
import { useParticipantsStore } from '../../store/participants.store';
import { usePublishedFileStore } from '../store/file.store';

export default defineComponent({
  components: {
    UploadModal,
    FileTable,
    SearchBar,
    ParticipantFilter,
  },
  props: {
    owner: {
      type: Object as PropType<Participant>,
      default: () => ({} as Participant),
    },
    participantId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const fileStore = usePublishedFileStore();
    const { loading, files, filter, totalFiles, totalFilesToUpload } = storeToRefs(fileStore);
    const { toast } = useToast();
    const participantsStore = useParticipantsStore();
    const { participants } = storeToRefs(participantsStore);
    const showUploadModal = ref(false);
    const showFilterDrawer = ref(false);
    const { features } = storeToRefs(useFeatureStore());

    const currentSearchTerm = computed(() => filter.value.searchTerms?.[0] ?? '');

    const isUploadRestricted = computed(
      () =>
        features.value[Feature.FileSizeLimit].restricted ||
        !!features.value[Feature.FileSizeLimit].threshold
    );

    const fileSizeLimit = computed(() => {
      const { threshold } = features.value[Feature.FileSizeLimit];
      return threshold || FileConstants.sizeLimit;
    });

    const handleUpload = (files: File[]) => {
      fileStore.upload(files);
      showUploadModal.value = false;
    };

    const handleDownload = async ({ id }: { id: string }) => {
      try {
        const downloadUrl = await FileService.getDownloadUrl(id);
        FileHelper.downloadFile(downloadUrl);
      } catch {
        toast.danger(t('files_could_not_download'));
      }
    };

    const handleDelete = ({ id }: { id: string }) => {
      fileStore.deleteFile(id);
    };

    const { isLoading, execute: searchFiles } = useAsyncState(
      async (searchTerm: string) => {
        await fileStore.updateFilter({ offset: 0, searchTerms: [searchTerm] });
      },
      null,
      {
        delay: 0,
        resetOnExecute: true,
        immediate: false,
      }
    );

    const searchLoading = computed(() => isLoading.value);

    const handleFilterByParticipants = (payload: Partial<LoadFilesPayload>) => {
      fileStore.updateFilter({ ...payload, offset: 0 });
      showFilterDrawer.value = false;
    };

    const allParticipants = computed(() => [...participants.value, props.owner]);

    const showAmountSelected = computed(() => filter.value.participantIds.length > 0);

    return {
      participants,
      loading,
      showUploadModal,
      filter,
      files,
      totalFiles,
      currentSearchTerm,
      searchLoading,
      totalFilesToUpload,
      isUploadRestricted,
      fileSizeLimit,
      showAmountSelected,
      allParticipants,
      showFilterDrawer,
      handleFilterByParticipants,
      searchFiles,
      handleUpload,
      handleDownload,
      handleDelete,
      updateFilter: fileStore.updateFilter,
      t,

      ButtonType,
      SlideOrigin,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/scss/layout';
@import '@getaccept/dsr-shared-new/src/scss/files';
</style>
